import React, { useEffect, useRef, useState } from "react";

import { AutoSliderStyles } from "./AutoSliderStyles";
import { Slide } from "../../types/RandomGlobalTypes";

type Props = {
  slides: Slide[];
  interval?: number;
  width?: number;
  height?: number;
  radius?: number;
  objectFit?: "contain" | "cover";
  showArrows?: boolean;
  showDots?: boolean;
  responsive?: boolean;
  isRTL?: boolean;
};

const AutoSlider = ({
  slides,
  interval = 3500,
  width = 579,
  height = 300,
  radius = 16,
  objectFit = "contain",
  showArrows = true,
  showDots = true,
  responsive = true,
  isRTL = false,
}: Props) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const startX = useRef<number | null>(null);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  const hasSlides = Array.isArray(slides) && slides.length > 0;
  const isSingle = slides?.length === 1;

  const getType = (sl: Slide) =>
    sl.type || (sl.src.match(/\.(mp4|webm|ogg)$/i) ? "video" : "image");

  // The `slides` prop can change while `index` persists (e.g. navigating
  // between service pages). Clamp every index-based read so an out-of-range,
  // undefined slide can never be dereferenced during render.
  const safeIndex = hasSlides ? Math.min(index, slides.length - 1) : 0;

  const currentIsVideo = hasSlides && getType(slides[safeIndex]) === "video";

  // Start a fresh slideshow at the first slide whenever the slides change.
  useEffect(() => {
    setIndex(0);
  }, [slides]);

  // Auto-advance only when current slide is NOT a video
  useEffect(() => {
    if (!hasSlides || isSingle || paused || currentIsVideo) return;
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      interval
    );
    return () => clearInterval(id);
  }, [hasSlides, isSingle, slides?.length, interval, paused, currentIsVideo]);

  const go = (dir: number) =>
    setIndex((i) => (i + dir + (slides?.length ?? 0)) % (slides?.length ?? 1));

  // When a video ends, advance to next slide
  const onVideoEnded = () => {
    if (!paused) {
      setIndex((i) => (i + 1) % slides.length);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    setPaused(true);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current == null) return;
    const delta = e.changedTouches[0].clientX - startX.current;
    const threshold = 40;
    if (delta > threshold) go(-1);
    else if (delta < -threshold) go(1);
    startX.current = null;
    setPaused(false);
  };

  // Play current video, pause + reset others
  useEffect(() => {
    if (!hasSlides) return;
    videoRefs.current.forEach((vid, i) => {
      if (!vid) return;
      try {
        if (i === safeIndex) {
          vid.currentTime = 0;
          vid.muted = true;
          const p = vid.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } else {
          vid.pause();
          vid.currentTime = 0;
        }
      } catch {
        /* no-op */
      }
    });
  }, [index, hasSlides, slides]);

  const s = AutoSliderStyles(
    hasSlides ? slides.length : 0,
    safeIndex,
    width,
    height,
    radius,
    objectFit,
    responsive,
    isRTL
  );

  if (!hasSlides) {
    return (
      <div
        style={{ width, height, borderRadius: radius, background: "#0000" }}
      />
    );
  }

  return (
    <div
      style={s.wrap}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div style={s.track}>
        {slides.map((sl, i) => {
          const type = getType(sl);

          return (
            <div key={i} style={s.slide}>
              {type === "video" ? (
                <video
                  ref={(el) => {
                    videoRefs.current[i] = el;
                  }}
                  src={sl.src}
                  style={s.imgStyle}
                  autoPlay={false}
                  controls={false}
                  muted
                  loop={false}
                  playsInline
                  preload="auto"
                  onEnded={onVideoEnded}
                />
              ) : (
                <img
                  src={sl.src}
                  alt={sl.alt ?? `slide-${i}`}
                  style={s.imgStyle}
                />
              )}
            </div>
          );
        })}
      </div>

      {showArrows && slides.length > 1 && (
        <>
          <button
            onClick={() => go(-1)}
            style={{ ...s.arrowBtn, insetInlineStart: 8 }}
          >
            ‹
          </button>
          <button
            onClick={() => go(1)}
            style={{ ...s.arrowBtn, insetInlineEnd: 8 }}
          >
            ›
          </button>
        </>
      )}

      {showDots && slides.length > 1 && (
        <div style={s.dotsWrap}>
          {slides.map((_, i) => (
            <span
              key={i}
              onClick={() => setIndex(i)}
              style={s.dot(i === safeIndex)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AutoSlider;